import { useState } from "react";
import { api, ROUTES } from "./api";
import {
  useAdmin,
  useAsync,
  Modal,
  Field,
  ErrorState,
  Loading,
  Empty,
  PageHead,
  money,
} from "./ui";

// "2026-12-31T18:29:59.000Z" -> "2026-12-31" (for the date input and the table)
const dateOnly = (v) => {
  if (!v) return "";
  const d = new Date(v);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export default function Coupons() {
  const { toast, confirm } = useAdmin();
  const { data, loading, error, reload } = useAsync(() =>
    api.get(ROUTES.coupons),
  );
  const [editing, setEditing] = useState(null); // null | {} (new) | coupon
  const coupons = data || [];

  const remove = async (c) => {
    const ok = await confirm({
      title: "Delete coupon",
      body: `Delete coupon "${c.code}"? Old orders keep the code they used.`,
    });
    if (!ok) return;
    try {
      await api.del(`${ROUTES.coupons}/deletecoupon/${c.id}`);
      toast("Coupon deleted");
      reload();
    } catch (e) {
      toast(e.message, "err");
    }
  };

  if (error) return <ErrorState error={error} onRetry={reload} />;

  const status = (c) => {
    if (!c.is_active) return <span className="badge">Inactive</span>;
    if (c.expires_at && new Date(c.expires_at) < new Date())
      return <span className="badge danger">Expired</span>;
    if (c.usage_limit !== null && c.used_count >= c.usage_limit)
      return <span className="badge warn">Used up</span>;
    return <span className="badge ok">Active</span>;
  };

  return (
    <>
      <PageHead
        title="Coupons"
        sub={`${coupons.length} coupons`}
        actions={
          <button className="btn primary" onClick={() => setEditing({})}>
            Add coupon
          </button>
        }
      />

      {loading ? (
        <Loading />
      ) : coupons.length === 0 ? (
        <Empty
          title="No coupons yet"
          body="Create a coupon code for your customers."
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Min order</th>
                <th>Used</th>
                <th>Expires</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.id}>
                  <td>
                    <b>{c.code}</b>
                  </td>
                  <td>
                    {c.discount_type === "percent"
                      ? `${Number(c.discount_value)}%${
                          c.max_discount ? ` (max ${money(c.max_discount)})` : ""
                        }`
                      : money(c.discount_value)}
                  </td>
                  <td>{Number(c.min_order) ? money(c.min_order) : "-"}</td>
                  <td>
                    {c.used_count}
                    {c.usage_limit !== null ? ` / ${c.usage_limit}` : ""}
                  </td>
                  <td>{c.expires_at ? dateOnly(c.expires_at) : "Never"}</td>
                  <td>{status(c)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 8,
                      }}
                    >
                      <button className="btn sm" onClick={() => setEditing(c)}>
                        Edit
                      </button>
                      <button
                        className="btn sm danger-ghost"
                        onClick={() => remove(c)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CouponForm
          coupon={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </>
  );
}

function CouponForm({ coupon, onClose, onSaved }) {
  const { toast } = useAdmin();
  const [f, setF] = useState({
    code: coupon?.code ?? "",
    discount_type: coupon?.discount_type ?? "percent",
    discount_value: coupon?.discount_value ?? "",
    min_order: coupon?.min_order ?? "",
    max_discount: coupon?.max_discount ?? "",
    expires_at: dateOnly(coupon?.expires_at),
    usage_limit: coupon?.usage_limit ?? "",
    is_active: coupon ? !!coupon.is_active : true,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!f.code.trim()) er.code = "Enter a code";
    if (f.discount_value === "" || Number(f.discount_value) <= 0)
      er.discount_value = "Enter a value above 0";
    if (f.discount_type === "percent" && Number(f.discount_value) > 100)
      er.discount_value = "Percent cannot be above 100";
    if (
      f.usage_limit !== "" &&
      (!Number.isInteger(Number(f.usage_limit)) || Number(f.usage_limit) < 1)
    )
      er.usage_limit = "Use a whole number of 1 or more";
    setErrors(er);
    if (Object.keys(er).length) return;

    const body = {
      code: f.code.trim().toUpperCase(),
      discount_type: f.discount_type,
      discount_value: Number(f.discount_value),
      min_order: f.min_order === "" ? 0 : Number(f.min_order),
      max_discount:
        f.discount_type === "percent" && f.max_discount !== ""
          ? Number(f.max_discount)
          : null,
      expires_at: f.expires_at ? `${f.expires_at} 23:59:59` : null,
      usage_limit: f.usage_limit === "" ? null : Number(f.usage_limit),
      is_active: f.is_active,
    };

    setSaving(true);
    try {
      if (coupon)
        await api.put(`${ROUTES.coupons}/updatecoupon/${coupon.id}`, body);
      else await api.post(`${ROUTES.coupons}/addcoupon`, body);
      toast(coupon ? "Coupon saved" : "Coupon added");
      onSaved();
    } catch (err) {
      toast(err.message, "err");
      setSaving(false);
    }
  };

  return (
    <Modal
      title={coupon ? "Edit coupon" : "Add coupon"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" form="coupon-form" disabled={saving}>
            {saving ? "Saving…" : coupon ? "Save changes" : "Add coupon"}
          </button>
        </>
      }
    >
      <form id="coupon-form" onSubmit={submit} className="form-grid" noValidate>
        <Field label="Code" error={errors.code}>
          <input
            className="input"
            value={f.code}
            onChange={set("code")}
            autoFocus
            style={{ textTransform: "uppercase" }}
          />
        </Field>
        <Field label="Type">
          <select
            className="input"
            value={f.discount_type}
            onChange={set("discount_type")}
          >
            <option value="percent">Percent (%)</option>
            <option value="fixed">Fixed amount (Rs)</option>
          </select>
        </Field>
        <Field
          label={f.discount_type === "percent" ? "Discount (%)" : "Discount (Rs)"}
          error={errors.discount_value}
        >
          <input
            className="input"
            type="number"
            min="0"
            step="0.01"
            value={f.discount_value}
            onChange={set("discount_value")}
          />
        </Field>
        <Field label="Minimum order (Rs)" hint="Leave empty for no minimum.">
          <input
            className="input"
            type="number"
            min="0"
            step="0.01"
            value={f.min_order}
            onChange={set("min_order")}
          />
        </Field>
        {f.discount_type === "percent" && (
          <Field label="Max discount (Rs)" hint="Optional cap for percent coupons.">
            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              value={f.max_discount}
              onChange={set("max_discount")}
            />
          </Field>
        )}
        <Field label="Expires on" hint="Optional. Valid until the end of this day.">
          <input
            className="input"
            type="date"
            value={f.expires_at}
            onChange={set("expires_at")}
          />
        </Field>
        <Field
          label="Usage limit"
          error={errors.usage_limit}
          hint="Optional. Empty means unlimited."
        >
          <input
            className="input"
            type="number"
            min="1"
            step="1"
            value={f.usage_limit}
            onChange={set("usage_limit")}
          />
        </Field>
        <div className="span-2">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={f.is_active}
              onChange={(e) => setF({ ...f, is_active: e.target.checked })}
            />
            Active
          </label>
        </div>
      </form>
    </Modal>
  );
}
